<template>
  <v-card class="card-container">
    <CreateCriticalityDialog
      v-if="!editingItem"
      :show-dialog="showDialog"
      :form="form"
      :field-errors="fieldErrors"
      :unidades="unidades"
      :dialog-departments="dialogDepartments"
      :dialog-sub-departments="dialogSubDepartments"
      :dialog-workshops="dialogWorkshops"
      :urgency-levels="urgencyLevels"
      :save-loading="saveLoading"
      @close="closeDialog"
      @save="saveForm"
      @unidade-change="onDialogUnidadeChange"
    />

    <EditCriticalityDialog
      v-else
      :show-dialog="showDialog"
      :form="form"
      :field-errors="fieldErrors"
      :urgency-levels="urgencyLevels"
      :save-loading="saveLoading"
      @close="closeDialog"
      @save="saveForm"
    />

    <div class="main-toolbar mb-6">
      <div class="icon-back">
        <v-icon large color="white">mdi mdi-alert</v-icon>
      </div>

      <span class="main-toolbar-title">Matriz de Criticidade</span>

      <v-spacer></v-spacer>

      <v-btn
        v-if="criticalitiesSelected.length > 0"
        class="mr-2 quaternary-button"
        depressed
        color="#FF4F64"
        dark
        @click="deleteMultiple"
      >
        Excluir Selecionados ({{ criticalitiesSelected.length }})
      </v-btn>

      <v-btn
        class="mr-2 primary-button"
        depressed
        color="#BBCF20"
        dark
        @click="toggleFilters"
      >
        Filtros
      </v-btn>

      <v-btn
        class="secondary-button"
        depressed
        dark
        @click="openAddDialog"
      >
        Adicionar Criticidade
      </v-btn>
    </div>

    <v-expand-transition>
      <div v-show="showFilters" class="mx-1 px-2 pt-3 pb-2">
        <v-row class="ma-0" dense>
          <v-col cols="12" sm="6" md="4">
            <label class="label">Unidade</label>
            <v-autocomplete
              v-model="selectedUnidade"
              :items="unidades"
              outlined
              hide-details="auto"
              class="primary-input"
              item-text="name"
              item-value="id"
              clearable
              append-icon=""
              @change="onUnidadeChange"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <label class="label">Setor</label>
            <v-autocomplete
              v-model="selectedDepartment"
              :items="departments"
              outlined
              hide-details="auto"
              class="primary-input"
              item-text="name"
              item-value="id"
              clearable
              append-icon=""
              :disabled="!departments.length"
              @change="selectedSubDepartment = null"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <label class="label">Sub-setor</label>
            <v-autocomplete
              v-model="selectedSubDepartment"
              :items="filterSubDepartments"
              outlined
              hide-details="auto"
              class="primary-input"
              item-text="name"
              item-value="id"
              clearable
              append-icon=""
              :disabled="!filterSubDepartments.length"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <label class="label">Oficina</label>
            <v-autocomplete
              v-model="selectedWorkshop"
              :items="workshops"
              outlined
              hide-details="auto"
              class="primary-input"
              item-text="name"
              item-value="id"
              clearable
              append-icon=""
              :disabled="!workshops.length"
            ></v-autocomplete>
          </v-col>
        </v-row>

        <v-row class="ma-0">
          <v-col cols="12">
            <div class="d-flex justify-end mb-2" style="width: 100%">
              <v-btn
                class="mx-2 primary-button"
                color="#0F3565"
                @click="filterTable"
                depressed
              >
                <span>Filtrar</span>
                <v-icon class="pl-2">mdi-magnify</v-icon>
              </v-btn>

              <v-btn
                depressed
                color="#20202066"
                class="clear-button mr-0"
                @click="clearFilters"
              >
                <span>Limpar</span>
                <v-icon class="pl-2">mdi-cached</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-expand-transition>

    <div class="wrapper desktop">
      <v-data-table
        :loading="loading"
        :loading-text="$t('Carregando...')"
        :no-data-text="$t('Nenhuma criticidade configurada')"
        :headers="headers"
        :items="filteredItems"
        v-model="criticalitiesSelected"
        :single-select="false"
        item-key="id"
        show-select
        class="main-table"
        hide-default-footer
        hide-default-header
        :items-per-page="pageSize"
        :header-props="headerProps"
        @update:options="handleSortUpdate"
      >
      <template #header="{ props, on }">
        <thead>
          <tr>
            <th
              :class="`text-${header.align || 'left'}`"
              v-for="header in props.headers"
              :key="header.value"
            >
              <v-simple-checkbox
                v-if="header.value === 'data-table-select'"
                v-model="props.everyItem"
                :indeterminate="props.someItems && !props.everyItem"
                color="purple"
                @input="on['toggle-select-all']"
              ></v-simple-checkbox>

              <div v-else>
                <v-hover v-slot="{ hover }">
                  <div>
                    <span>{{ $t(header.text) }}</span>
                    <v-icon
                      v-if="
                        hover &&
                        header.value != 'data-table-select' &&
                        header.value != 'actions'
                      "
                      color="white"
                      class="mx-2"
                      @click="on.sort(header.value)"
                    >
                      {{
                        props.options.sortBy.includes(header.value)
                          ? `mdi-chevron-down`
                          : `mdi-chevron-up`
                      }}
                    </v-icon>
                  </div>
                </v-hover>
              </div>
            </th>
          </tr>
        </thead>
      </template>

      <template #[`item.department`]="{ item }">
        <div class="text-center">{{ item.department?.name || 'Sem registro' }}</div>
      </template>

      <template #[`item.subDepartment`]="{ item }">
        <div class="text-center">{{ item.subDepartment?.name || '-' }}</div>
      </template>

      <template #[`item.unidade`]="{ item }">
        <div class="text-center">{{ getUnidadeName(item) }}</div>
      </template>

      <template #[`item.workshop`]="{ item }">
        <div class="text-center">{{ item.workshop?.name || 'Sem registro' }}</div>
      </template>

      <template #[`item.urgencyLevelLabel`]="{ item }">
        <div class="d-flex justify-center">
          <v-chip
            :color="getUrgencyColor(item.urgencyLevel)"
            text-color="white"
            small
          >
            {{ getUrgencyLabel(item.urgencyLevel) }}
          </v-chip>
        </div>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="ma-1"
              color="#0F3565"
              dark
              small
              style="font-weight: bold"
              v-bind="attrs"
              v-on="on"
              depressed
              @click.stop="openEditDialog(item)"
            >
              <v-icon small dark>mdi-pencil-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('Editar') }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="ma-1"
              small
              color="#FF4F64"
              style="font-weight: bold"
              v-bind="attrs"
              @click.stop="deleteItem(item)"
              v-on="on"
              depressed
            >
              <v-icon color="white">mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('Excluir') }}</span>
        </v-tooltip>
      </template>
      </v-data-table>

      <div class="pagination" v-if="totalPages > 1">
        <div class="item-pagination block-one">
          <v-menu bottom offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                class="btn"
                depressed
                color="#C0C0C0"
                small
              >
                qtd: {{ pageSize }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="(size, i) in sizes"
                :key="i"
                @click="pageSize = size"
              >
                <v-list-item-title>{{ size }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <div class="registros" v-if="totalItensOnPage">
            <span> 1 - {{ totalItensOnPage }} de {{ totalItems }} </span>
          </div>
        </div>

        <div class="item-pagination block-two">
          <v-pagination
            class="pagination-item"
            v-model="page"
            :length="totalPages"
            :total-visible="5"
          ></v-pagination>
        </div>
      </div>
    </div>

    <DeleteConfirmDialog
      :show-dialog="showDeleteConfirm"
      :title="`${$t('Remover')} Criticidade`"
      :message="$t('Tem certeza de que deseja remover esta criticidade?')"
      :loading="loading"
      :cancel-label="$t('Cancelar')"
      :confirm-label="$t('Remover')"
      @close="showDeleteConfirm = false"
      @confirm="confirmDelete"
    />

    <DeleteConfirmDialog
      :show-dialog="showDeleteMultipleConfirm"
      :title="`${$t('Remover')} ${criticalitiesSelected.length} Criticidades`"
      :message="$t('Tem certeza de que deseja remover estas criticidades?')"
      :loading="loading"
      :cancel-label="$t('Cancelar')"
      :confirm-label="$t('Remover')"
      @close="showDeleteMultipleConfirm = false"
      @confirm="confirmDeleteMultiple"
    />

  </v-card>
</template>

<script>
import ApiService from '@/services/ApiService'
import CreateCriticalityDialog from './components/CreateCriticalityDialog.vue'
import EditCriticalityDialog from './components/EditCriticalityDialog.vue'
import DeleteConfirmDialog from './components/DeleteConfirmDialog.vue'

export default {
  name: 'CriticalityMatrix',
  components: {
    CreateCriticalityDialog,
    EditCriticalityDialog,
    DeleteConfirmDialog,
  },

  data() {
    return {
      loading: false,
      saveLoading: false,
      showDialog: false,
      editingItem: null,
      page: 1,
      pageSize: 10,
      totalPages: 1,
      totalItems: 0,
      totalItensOnPage: 0,
      sizes: [10, 30, 50, 100],
      tableOptions: {},
      
      unidades: [],
      departments: [],
      workshops: [],
      dialogDepartments: [],
      dialogWorkshops: [],
      criticalityData: [],

      selectedUnidade: null,
      institutionId: null,
      selectedDepartment: null,
      selectedSubDepartment: null,
      selectedWorkshop: null,
      appliedDepartment: null,
      appliedSubDepartment: null,
      appliedWorkshop: null,
      criticalitiesSelected: [],
      showFilters: false,
      showDeleteConfirm: false,
      showDeleteMultipleConfirm: false,
      itemToDelete: null,

      form: {
        id: null,
        unidadeId: null,
        institutionId: null,
        departmentId: null,
        subDepartmentId: null,
        workshopId: null,
        departmentName: '',
        subDepartmentName: '',
        workshopName: '',
        urgencyLevel: null,
      },
      fieldErrors: {
        unidadeId: false,
        departmentId: false,
        workshopId: false,
        urgencyLevel: false,
      },

      urgencyLevels: [
        { label: 'Alta', value: 0, color: '#FF4F64' },
        { label: 'Média', value: 1, color: '#FFD21F' },
        { label: 'Baixa', value: 2, color: '#B9CE1E' },
      ],

      headers: [
        {
          text: '',
          align: 'center',
          value: 'data-table-select',
        },
        {
          text: 'Unidade',
          align: 'center',
          value: 'unidade',
          sortable: false,
        },
        {
          text: 'Setor',
          align: 'center',
          value: 'department',
          sortable: true,
        },
        {
          text: 'Sub-setor',
          align: 'center',
          value: 'subDepartment',
          sortable: false,
        },
        {
          text: 'Oficina',
          align: 'center',
          value: 'workshop',
          sortable: true,
        },
        {
          text: 'Criticidade',
          align: 'center',
          value: 'urgencyLevelLabel',
          sortable: false,
        },
        {
          text: 'Ações',
          value: 'actions',
          sortable: false,
          width: 200,
          align: 'center',
        },
      ],

      headerProps: {
        sortByText: 'Ordenar por',
      },

      apiService: new ApiService(),
    }
  },

  computed: {
    filteredItems() {
      return this.criticalityData
    },

    filterSubDepartments() {
      return this.subDepartmentsOf(this.departments, this.selectedDepartment)
    },

    dialogSubDepartments() {
      return this.subDepartmentsOf(this.dialogDepartments, this.form.departmentId)
    },

    canAddNew() {
      return this.selectedUnidade && (this.selectedDepartment || this.selectedWorkshop)
    },
  },

  created() {
    this.loadUnidades()
  },

  methods: {
    subDepartmentsOf(departments, departmentId) {
      const department = departments.find((d) => d.id === departmentId)
      return department?.subDepartments || []
    },

    async loadUnidades() {
      this.loading = true
      try {
        const instData = JSON.parse(localStorage.getItem('institution'))
        this.institutionId = instData.id
        
        const filter = {
          page: 1,
          pageSize: 1000,
          institutionId: this.institutionId,
        }
        
        const response = await this.apiService.post('unidade/list', filter)
        const unidades = response.content?.unidades || []
        this.unidades = unidades.filter((unidade) => Number(unidade.status) === 0)

        this.selectedUnidade = null

        await this.loadDepartmentsForFilters()

        await this.loadCriticalityMatrix()
      } catch (error) {
        console.error('Erro ao carregar unidades:', error)
        this.$toast.error('Erro ao carregar dados das unidades')
      } finally {
        this.loading = false
      }
    },

    async onUnidadeChange() {
      if (!this.selectedUnidade) {
        await this.loadDepartmentsForFilters()
        this.workshops = []
        this.selectedDepartment = null
        this.selectedSubDepartment = null
        this.selectedWorkshop = null
        this.appliedDepartment = null
        this.appliedSubDepartment = null
        this.appliedWorkshop = null
        await this.loadCriticalityMatrix()
        return
      }

      this.loading = true
      try {
        const selectedUnidade = this.unidades.find(
          (u) => Number(u.id) === Number(this.selectedUnidade)
        )
        this.institutionId =
          selectedUnidade?.institutionId ||
          selectedUnidade?.institution?.id ||
          this.institutionId

        await this.loadDepartmentsForFilters()

        const workshopResponse = await this.apiService.get(
          `workshop/${this.institutionId}`
        )
        this.workshops = workshopResponse.content || []

        this.selectedDepartment = null
        this.selectedSubDepartment = null
        this.selectedWorkshop = null
        this.appliedDepartment = null
        this.appliedSubDepartment = null
        this.appliedWorkshop = null

        await this.loadCriticalityMatrix()
      } catch (error) {
        console.error('Erro ao carregar dados:', error)
        this.$toast.error('Erro ao carregar dados')
      } finally {
        this.loading = false
      }
    },

    async loadDepartmentsForFilters() {
      if (this.selectedUnidade) {
        const deptResponse = await this.apiService.get(
          `department/list/unidade/${this.selectedUnidade}`
        )
        this.departments = deptResponse.content || []
        return
      }

      if (!this.unidades.length) {
        this.departments = []
        return
      }

      const departmentResponses = await Promise.all(
        this.unidades.map((unidade) =>
          this.apiService.get(`department/list/unidade/${unidade.id}`)
        )
      )

      const allDepartments = departmentResponses
        .map((response) => response.content || [])
        .flat()

      const uniqueDepartmentsMap = new Map()
      allDepartments.forEach((dept) => {
        if (!uniqueDepartmentsMap.has(dept.id)) {
          uniqueDepartmentsMap.set(dept.id, dept)
        }
      })

      this.departments = Array.from(uniqueDepartmentsMap.values())
    },

    async loadCriticalityMatrix() {
      this.loading = true
      try {
        const filter = {
          institutionId: this.institutionId,
          departmentId: this.appliedDepartment || null,
          subDepartmentId: this.appliedSubDepartment || null,
          workshopId: this.appliedWorkshop || null,
          page: this.page,
          pageSize: this.pageSize,
        }

        const response = await this.apiService.post(
          'departmentWorkshopUrgency/list-filtered',
          filter
        )

        const content = response.content || []
        const isArrayResponse = Array.isArray(content)
        const pager = !isArrayResponse ? content.pager : null
        const possibleListKeys = ['departmentWorkshopUrgencies', 'items', 'list', 'data']

        let items = []
        if (isArrayResponse) {
          items = content
        } else {
          const listByKnownKey = possibleListKeys
            .map((key) => content[key])
            .find((value) => Array.isArray(value))

          const firstArrayInObject = Object.values(content).find((value) => Array.isArray(value))
          items = listByKnownKey || firstArrayInObject || []
        }

        this.criticalityData = items
        this.totalItensOnPage = items.length

        if (pager) {
          this.totalItems = Number(pager.totalItems || items.length)
          this.totalPages = Number(pager.totalPages || 1)
        } else {
          this.totalItems = items.length
          this.totalPages = 1
        }
      } catch (error) {
        console.error('Erro ao carregar matriz de criticidade:', error)
        this.$toast.error('Erro ao carregar dados de criticidade')
      } finally {
        this.loading = false
      }
    },

    filterTable() {
      this.appliedDepartment = this.selectedDepartment
      this.appliedSubDepartment = this.selectedSubDepartment
      this.appliedWorkshop = this.selectedWorkshop
      this.page = 1
      this.loadCriticalityMatrix()
    },

    handleSortUpdate(options) {
      this.tableOptions = options
    },

    async clearFilters() {
      this.selectedUnidade = null
      this.selectedDepartment = null
      this.selectedSubDepartment = null
      this.selectedWorkshop = null
      this.appliedDepartment = null
      this.appliedSubDepartment = null
      this.appliedWorkshop = null
      this.page = 1

      await this.onUnidadeChange()
    },

    toggleFilters() {
      this.showFilters = !this.showFilters
    },

    ensureApiSuccess(response, fallbackMessage) {
      return response?.statusCode === 200
        ? response
        : Promise.reject(new Error(response?.message || fallbackMessage))
    },

    async onDialogUnidadeChange() {
      if (!this.form.unidadeId) {
        this.dialogDepartments = []
        this.dialogWorkshops = []
        this.form.departmentId = null
        this.form.subDepartmentId = null
        this.form.workshopId = null
        this.form.institutionId = null
        return
      }

      const selectedUnidade = this.unidades.find(
        (u) => Number(u.id) === Number(this.form.unidadeId)
      )

      const selectedInstitutionId =
        selectedUnidade?.institutionId ||
        selectedUnidade?.institution?.id ||
        this.institutionId

      this.form.institutionId = Number(selectedInstitutionId)

      try {
        const [deptResponse, workshopResponse] = await Promise.all([
          this.apiService.get(`department/list/unidade/${this.form.unidadeId}`),
          this.apiService.get(`workshop/${this.form.institutionId}`),
        ])

        this.dialogDepartments = deptResponse.content || []
        this.dialogWorkshops = workshopResponse.content || []
      } catch (error) {
        this.dialogDepartments = []
        this.dialogWorkshops = []
        this.$toast.error('Erro ao carregar setores e oficinas da unidade')
      }
    },

    async openAddDialog() {
      this.showDialog = true
      this.editingItem = null
      this.fieldErrors = {
        unidadeId: false,
        departmentId: false,
        workshopId: false,
        urgencyLevel: false,
      }
      this.form = {
        id: null,
        unidadeId: this.selectedUnidade || null,
        institutionId: null,
        departmentId: null,
        subDepartmentId: null,
        workshopId: null,
        departmentName: '',
        subDepartmentName: '',
        workshopName: '',
        urgencyLevel: null,
      }

      this.dialogDepartments = []
      this.dialogWorkshops = []

      if (this.form.unidadeId) {
        try {
          await this.onDialogUnidadeChange()
        } catch (error) {

        }
      }
    },

    openEditDialog(item) {
      this.editingItem = item
      this.fieldErrors = {
        unidadeId: false,
        departmentId: false,
        workshopId: false,
        urgencyLevel: false,
      }
      this.form = {
        id: item.id,
        institutionId: item.institutionId,
        departmentId: item.departmentId,
        subDepartmentId: item.subDepartmentId,
        workshopId: item.workshopId,
        departmentName: item.department?.name || '',
        subDepartmentName: item.subDepartment?.name || '',
        workshopName: item.workshop?.name || '',
        urgencyLevel: Number(item.urgencyLevel),
      }
      this.showDialog = true
    },

    closeDialog() {
      this.showDialog = false
      this.editingItem = null
      this.fieldErrors = {
        unidadeId: false,
        departmentId: false,
        workshopId: false,
        urgencyLevel: false,
      }
      this.form = {
        id: null,
        unidadeId: null,
        institutionId: null,
        departmentId: null,
        subDepartmentId: null,
        workshopId: null,
        departmentName: '',
        subDepartmentName: '',
        workshopName: '',
        urgencyLevel: null,
      }
    },

    async saveForm() {
      const urgencyIsValid = [0, 1, 2].includes(this.form.urgencyLevel)

      this.fieldErrors = {
        unidadeId: !this.editingItem && !this.form.unidadeId,
        departmentId: !this.form.departmentId,
        workshopId: !this.form.workshopId,
        urgencyLevel: !urgencyIsValid,
      }

      if (Object.values(this.fieldErrors).some(Boolean)) {
        return
      }

      this.saveLoading = true
      try {
        const endpoint = this.editingItem
          ? 'departmentWorkshopUrgency/edit/' + this.form.id
          : 'departmentWorkshopUrgency/create'

        const method = this.editingItem ? 'put' : 'post'

        const createPayload = {
          departmentId: Number(this.form.departmentId),
          subDepartmentId: this.form.subDepartmentId
            ? Number(this.form.subDepartmentId)
            : null,
          workshopId: Number(this.form.workshopId),
          urgencyLevel: Number(this.form.urgencyLevel),
          institutionId: Number(this.form.institutionId),
        }

        const editPayload = {
          id: Number(this.form.id),
          ...createPayload,
        }

        const payload = this.editingItem ? editPayload : createPayload

        await this.apiService[method](endpoint, payload)
        await this.loadCriticalityMatrix()
        this.$toast.success(
          this.editingItem
            ? 'Criticidade atualizada com sucesso!'
            : 'Criticidade adicionada com sucesso!'
        )
        this.closeDialog()
      } catch (error) {
        console.error('Erro ao salvar:', error)
        this.$toast.error('Erro ao salvar criticidade')
      } finally {
        this.saveLoading = false
      }
    },

    deleteItem(item) {
      this.itemToDelete = item
      this.showDeleteConfirm = true
    },

    async confirmDelete() {
      if (!this.itemToDelete) return

      this.loading = true
      try {
        await this.apiService.delete(
          `departmentWorkshopUrgency/delete/${this.itemToDelete.id}`
        )
        await this.loadCriticalityMatrix()
        this.$toast.success('Criticidade deletada com sucesso!')
      } catch (error) {
        console.error('Erro ao deletar:', error)
        this.$toast.error('Erro ao deletar criticidade')
      } finally {
        this.loading = false
        this.showDeleteConfirm = false
        this.itemToDelete = null
      }
    },

    deleteMultiple() {
      if (this.criticalitiesSelected.length === 0) {
        return
      }
      this.showDeleteMultipleConfirm = true
    },

    async confirmDeleteMultiple() {
      if (this.criticalitiesSelected.length === 0) {
        return
      }

      this.loading = true
      try {
        const selectedIds = this.criticalitiesSelected.map((item) => Number(item.id))

        const response = await this.apiService.delete(
          'departmentWorkshopUrgency/deletemany',
          selectedIds
        )
        await this.ensureApiSuccess(response, 'Erro ao deletar criticidades')

        await this.loadCriticalityMatrix()
        this.criticalitiesSelected = []
        this.$toast.success('Criticidades deletadas com sucesso!')
      } catch (error) {
        console.error('Erro ao deletar múltiplas criticidades:', error)
        this.$toast.error('Erro ao deletar criticidades')
      } finally {
        this.loading = false
        this.showDeleteMultipleConfirm = false
      }
    },

    getUrgencyLabel(level) {
      const item = this.urgencyLevels.find(u => u.value === level)
      return item ? item.label : 'Desconhecida'
    },

    getUrgencyColor(level) {
      const item = this.urgencyLevels.find(u => u.value === level)
      return item ? item.color : '#999999'
    },

    getUnidadeName(item) {
      const unidadeName =
        item?.unidade?.name ||
        item?.unidadeName ||
        item?.department?.unidade?.name ||
        item?.department?.unidadeName

      if (unidadeName) {
        return unidadeName
      }

      const unidadeId = item?.department?.unidadeId || item?.unidadeId
      if (!unidadeId) {
        return 'Sem registro'
      }

      const unidade = this.unidades.find((u) => Number(u.id) === Number(unidadeId))
      return unidade?.name || 'Sem registro'
    },

    exportData() {
      try {
        const data = this.filteredItems.map(item => ({
          'Unidade': this.getUnidadeName(item),
          'Setor': item.department?.name || '',
          'Sub-setor': item.subDepartment?.name || '',
          'Oficina': item.workshop?.name || '',
          'Criticidade': this.getUrgencyLabel(item.urgencyLevel),
        }))

        const csv = this.convertToCSV(data)
        this.downloadCSV(csv, 'matriz-criticidade.csv')
        this.$toast.success('Dados exportados com sucesso!')
      } catch (error) {
        console.error('Erro ao exportar:', error)
        this.$toast.error('Erro ao exportar dados')
      }
    },

    convertToCSV(data) {
      if (!data || data.length === 0) return ''

      const headers = Object.keys(data[0])
      const rows = data.map(obj => headers.map(header => `"${obj[header]}"`).join(','))

      return [headers.join(','), ...rows].join('\n')
    },

    downloadCSV(csv, filename) {
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)

      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },

  watch: {
    pageSize() {
      this.page = 1
      this.loadCriticalityMatrix()
    },

    page() {
      this.loadCriticalityMatrix()
    },
  },
}
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.main-table {
  margin-bottom: 30px;
}
</style>
